import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { AppListLayout } from '@/shared/app-list-layout';
import type { DataTableParams, DataTableView } from '@/shared/app-list-layout';
import type { ListColumn, ListRowAction } from '@/shared/app-list-layout/types';
import { useListFetch } from './use-list-fetch';
import type { ListPhase } from './use-list-fetch';
import type { SearchFields } from './query';

/**
 * The list surface every list page in this console shares.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every list page is the same page with different columns. Written separately they
 * drifted: one had a search box and the others did not, one counted its tabs from
 * the filtered rows and another from the whole collection, one switched to cards on
 * a phone and the rest ran off the screen. The user's ask was consistency, and the
 * only way to keep a dozen pages consistent is for them to be one page.
 *
 * WHAT IT OWNS
 * ------------
 * The four states (loading / empty / refused / failed), the saved-view tabs, the
 * search box, the column filters, the pager, the table/cards toggle, and the single
 * data path. The caller owns the columns, the read, and the words.
 *
 * THE COUNT RULE
 * --------------
 * A tab's count is a claim about the COLLECTION, never about the page on screen. The
 * counts are derived from the same read that answered the query, so a failed read is
 * the page's error surface rather than a tab strip of zeros.
 */

/**
 * The fallback failure, used only when the read failed without saying why.
 *
 * A failure that carries the provider's own words is reported in those words — the
 * operator needs the sentence that tells them whether to retry, wait, or call
 * someone, and a generic message would hide it.
 */
const READ_FAILED = 'These results could not be loaded.';

/**
 * The first query, in the table's own defaults.
 *
 * The table is only mounted once a read has settled, so it cannot ask the first
 * question itself. The page asks it here; the engine's mount announcement of the
 * same query is answered from the same read (see `useListFetch`).
 */
const INITIAL_QUERY: DataTableParams = { page: 1, pageSize: 25, sort: [], filters: {} };

/** What one list tells the shared surface about itself. */
export type AppListProps<T> = {
  /** The page heading. */
  readonly title: string;
  /** One sentence under the heading, saying what the list is for. */
  readonly description: string;
  /** The whole-collection read. Must be a stable `useCallback`. */
  readonly read: () => Promise<ReadonlyArray<T>>;
  /** The fields a search term is matched against. Must be a stable reference. */
  readonly searchFields: SearchFields<T>;
  /** The field a saved view counts by — the collection's own key, not the page's. */
  readonly countKeyOf: (row: T) => string;
  /** The saved views, built from the counts the read produced. */
  readonly buildViews: (counts: Partial<Record<string, number>>) => DataTableView[];
  /**
   * A banner derived from the whole-collection counts, or nothing.
   *
   * It takes counts rather than rows because a warning that only counted the current
   * page would under-report — and an under-reporting warning is worse than none.
   */
  readonly buildBanner?: (counts: Partial<Record<string, number>>) => ReactNode;
  /**
   * Reports the `status` column filter as it changes, or `undefined` when cleared.
   *
   * A page whose read can be narrowed by the server passes this so the filter reaches
   * the request rather than only the rows already on screen. A page whose read cannot
   * be narrowed simply omits it, and the filter narrows locally.
   */
  readonly onStatusChange?: (status: string | undefined) => void;
  /** The columns. Declarative descriptors — the engine renders every cell. */
  readonly columns: ReadonlyArray<ListColumn<T>>;
  /** The row's stable identity. */
  readonly getRowId: (row: T) => string;
  /**
   * The actions offered on each row.
   *
   * They are declared rather than rendered into a column so the engine can put
   * them where the presentation needs them: a trailing sticky cell in the table,
   * and the card's sheet on a phone.
   */
  readonly rowActions?: ReadonlyArray<ListRowAction<T>>;
  /** Buttons in the header, right of the title. */
  readonly toolbarActions?: ReactNode;
  /** The empty state's heading. */
  readonly emptyTitle: string;
  /** The empty state's one sentence. */
  readonly emptyDescription: string;
  /**
   * The copy for a collection that HAS rows but none matching the current query.
   *
   * It is a different sentence from `emptyDescription` on purpose: "Author the first
   * document" is advice for an empty deployment, and it is wrong — even insulting —
   * on a screen where three documents exist and a filter is hiding them.
   */
  readonly noMatchTitle?: string;
  readonly noMatchDescription?: string;
  /** The empty state's action, when there is one. */
  readonly emptyAction?: ReactNode;
  /**
   * Receives the surface's own re-read, so a toolbar control can ask for one.
   *
   * The upload control is the reason this exists: it finishes an upload and the
   * list must show the new asset, but the control is rendered by the page while
   * the re-read belongs to the surface. Handing the function down is cheaper than
   * making every page own a reload it would have to keep in sync.
   */
  readonly onReloadReady?: (reload: () => void) => void;
  /** Ask for the responsive presentation: cards below `sm`, the table above. */
  readonly responsive?: boolean;
};

/**
 * The header's actions: whatever the page supplied, then Refresh.
 *
 * Refresh is the shared surface's own control — every list can be re-read, and a
 * page that had to remember to add it would be a page that forgot.
 */
function ListToolbar({
  actions,
  onReload,
}: {
  actions: ReactNode;
  onReload: () => void;
}): ReactNode {
  return (
    <>
      {actions}
      <Button variant="secondary" size="sm" onClick={onReload}>
        <FiRefreshCw size={14} aria-hidden="true" />
        Refresh
      </Button>
    </>
  );
}

/**
 * The surface's own re-read, and the hand-off that lets a page ask for one.
 *
 * The upload control is the reason this exists: it finishes an upload and the list
 * must show the new asset, but the control is rendered by the page while the
 * re-read belongs to the surface. Handing the function down is cheaper than making
 * every page own a reload it would have to keep in sync.
 */
function useReload(
  onFetch: (params: DataTableParams, options?: { force?: boolean }) => void,
  setPhase: (phase: ListPhase) => void,
  onReloadReady: ((reload: () => void) => void) | undefined,
): () => void {
  const reload = useCallback((): void => {
    setPhase('loading');
    onFetch(INITIAL_QUERY, { force: true });
  }, [onFetch, setPhase]);

  useEffect(() => {
    onReloadReady?.(reload);
  }, [onReloadReady, reload]);

  return reload;
}

/**
 * Renders one list.
 *
 * The caller supplies the columns and the read; this owns everything else, so the
 * pages cannot drift apart again.
 */
export function AppList<T>(props: AppListProps<T>): ReactNode {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Partial<Record<string, number>>>({});
  const [searchValue, setSearchValue] = useState('');
  const [phase, setPhase] = useState<ListPhase>('loading');
  const [error, setError] = useState<Error | null>(null);

  const { read, searchFields, countKeyOf, buildViews, buildBanner, onStatusChange } = props;
  const onFetch = useListFetch(read, searchFields, countKeyOf, {
    setRows,
    setTotal,
    setCounts,
    setPhase,
    setError,
  });

  // The first read, and every read after the page's own read changes identity.
  //
  // A page whose read can be narrowed by the server (the executions ledger) hands a
  // new `read` when its filter changes, and the collection it is asking about is a
  // different one — so the question must be asked again even though the engine's
  // query is unchanged. `force` says exactly that: this is a new question, not the
  // echo of the one already answered.
  //
  // The dependency is `read`, NOT `onFetch`. `onFetch` is deliberately stable — it
  // holds the read in a ref so an inline `countKeyOf` cannot make it a new function
  // on every render — so keying this effect on it would mean a page whose read
  // changed never re-read. The read's identity is the thing that actually changed.
  useEffect(() => {
    onFetch(INITIAL_QUERY, { force: true });
  }, [onFetch, read]);

  const views = useMemo(() => buildViews(counts), [buildViews, counts]);
  const banner = useMemo(() => buildBanner?.(counts), [buildBanner, counts]);

  // The status filter is the one filter a page may push to the server, so it is
  // reported upward as it changes. The engine owns the filter's state; this only
  // relays it.
  const handleFiltersChange = useCallback(
    (filters: Record<string, string>): void => {
      onStatusChange?.(filters.status === '' ? undefined : filters.status);
    },
    [onStatusChange],
  );

  const reload = useReload(onFetch, setPhase, props.onReloadReady);

  return (
    <AppListLayout<T>
      title={props.title}
      description={props.description}
      columns={props.columns}
      rows={rows}
      total={total}
      getRowId={props.getRowId}
      rowActions={props.rowActions}
      views={views}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onFetch={onFetch}
      onColumnFiltersChange={onStatusChange ? handleFiltersChange : undefined}
      responsive={props.responsive}
      banner={banner}
      toolbarActions={<ListToolbar actions={props.toolbarActions} onReload={reload} />}
      isLoading={phase === 'loading'}
      // A refusal is not a failure: the error surface is cleared when the read was
      // refused, so denial, failure and empty stay three different states.
      error={phase === 'error' ? (error ?? new Error(READ_FAILED)) : null}
      isDenied={phase === 'denied'}
      // TWO EMPTIES, AND THEY ARE NOT THE SAME SCREEN.
      //
      // A collection with nothing in it is the layout's empty state, because that
      // state carries the page's own action ("New document") and a row of no data
      // cannot. A collection that HAS rows but none matching the current tab, search
      // or filter is the table's own empty row, because raising it to the layout
      // would replace the tabs, search box and filters — leaving the user with no
      // control to undo the query that emptied the screen.
      isEmpty={phase === 'ready' && total === 0 && counts.all === 0}
      emptyTitle={props.emptyTitle}
      emptyDescription={props.emptyDescription}
      emptyAction={props.emptyAction}
      // The table's own empty row, reached when the collection has rows but the
      // current tab, search or filter matched none of them. The title defaults to
      // "No results" rather than the collection-empty heading, because "The
      // catalogue is empty" is a false statement about a catalogue of three.
      noMatchTitle={props.noMatchTitle ?? 'No results'}
      noMatchDescription={props.noMatchDescription ?? props.emptyDescription}
      errorMessage={READ_FAILED}
    />
  );
}
